<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { formatBytes } from 'shared/helpers/FileHelper';
import CannedResponseAPI from 'dashboard/api/cannedResponse';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AttachmentThumb from './AttachmentThumb.vue';

const props = defineProps({
  // Files already on this canned response. ActiveStorage rejects attaching the same blob
  // twice to a record, so they are shown as already added rather than selectable.
  attachedSignedIds: { type: Array, default: () => [] },
  full: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const files = ref([]);
const isLoading = ref(true);
const searchQuery = ref('');

onMounted(async () => {
  try {
    const { data } = await CannedResponseAPI.library();
    files.value = data;
  } catch (error) {
    useAlert(t('CANNED_MGMT.ATTACHMENTS.LIBRARY.ERROR'));
  } finally {
    isLoading.value = false;
  }
});

// Client side: the list is bounded by the number of canned responses, so it always fits.
const filteredFiles = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return files.value;
  return files.value.filter(
    file =>
      file.filename.toLowerCase().includes(query) ||
      (file.used_in || '').toLowerCase().includes(query)
  );
});

const isAttached = file => props.attachedSignedIds.includes(file.signed_id);
</script>

<template>
  <div class="p-2 mb-2 border rounded-lg border-n-strong bg-n-alpha-1">
    <input
      v-model="searchQuery"
      type="search"
      class="!mb-2 !text-sm"
      :placeholder="$t('CANNED_MGMT.ATTACHMENTS.LIBRARY.SEARCH')"
    />

    <p v-if="isLoading" class="mb-0 text-xs text-n-slate-11">
      {{ $t('CANNED_MGMT.ATTACHMENTS.LIBRARY.LOADING') }}
    </p>
    <p v-else-if="!files.length" class="mb-0 text-xs text-n-slate-11">
      {{ $t('CANNED_MGMT.ATTACHMENTS.LIBRARY.EMPTY') }}
    </p>
    <p v-else-if="!filteredFiles.length" class="mb-0 text-xs text-n-slate-11">
      {{ $t('CANNED_MGMT.ATTACHMENTS.LIBRARY.NO_RESULTS') }}
    </p>

    <ul
      v-else
      class="flex flex-col gap-1 p-0 m-0 list-none max-h-52 overflow-y-auto"
    >
      <li
        v-for="file in filteredFiles"
        :key="file.signed_id"
        class="flex items-center gap-2 p-1 rounded"
      >
        <AttachmentThumb
          :filename="file.filename"
          :content-type="file.content_type"
          :preview-url="file.data_url"
        />
        <div class="flex-1 min-w-0">
          <p class="mb-0 text-sm truncate text-n-slate-12">
            {{ file.filename }}
          </p>
          <p class="mb-0 text-xs truncate text-n-slate-11">
            {{ formatBytes(file.byte_size, 1) }} ·
            {{ $t('CANNED_MGMT.ATTACHMENTS.LIBRARY.USED_IN') }}
            /{{ file.used_in }}
          </p>
        </div>
        <span
          v-if="isAttached(file)"
          class="text-xs shrink-0 text-n-teal-11 flex items-center gap-1"
        >
          <span class="i-ri-check-line" />
          {{ $t('CANNED_MGMT.ATTACHMENTS.LIBRARY.ADDED') }}
        </span>
        <NextButton
          v-else
          faded
          slate
          xs
          type="button"
          icon="i-ri-add-line"
          :disabled="full"
          :title="$t('CANNED_MGMT.ATTACHMENTS.LIBRARY.ADD')"
          @click.prevent="emit('select', file)"
        />
      </li>
    </ul>
  </div>
</template>
